import { useApolloClient } from "@apollo/client";
import { getDataOrNull } from "@msys/common";
import { isArray, isPlainObject } from "lodash-es";
import {
  namedOperations,
  PimCustomSupplierProduct,
  PimSupplierProduct,
} from "../../../clients/graphqlTypes.js";
import { DEPLOY_ENV } from "../../../environment.js";
import { useUserData } from "../../auth/useUserData.js";
import { useUserPreference } from "../users/useUserPreference.js";
import {
  useS4HanaIntegrationSettingsQuery,
  useS4HanaQuoteItemDataHooksQuery,
} from "./hooks.generated.js";
import { useSapProfileActionSettingsQuery } from "./SapProfileActionSettings.generated.js";

export function useHasSapS4HanaIntegration() {
  const userData = useUserData();

  if (!userData.currentUser) {
    return false;
  }

  return userData.currentUser.organisation.enabledIntegrationIds.includes(
    "sap_s4hana"
  );
}

export function useSapS4HanaIntegrationSettings() {
  const hasIntegration = useHasSapS4HanaIntegration();
  const client = useApolloClient();

  const query = useS4HanaIntegrationSettingsQuery({
    client,
    skip: !hasIntegration,
  });

  return hasIntegration
    ? (getDataOrNull(query.data?.organisationIntegration)
        ?.organisationIntegration?.settings ?? null)
    : null;
}

export function useSapProfileActionSettings() {
  const hasIntegration = useHasSapS4HanaIntegration();
  const client = useApolloClient();

  const query = useSapProfileActionSettingsQuery({
    client,
    skip: !hasIntegration,
  });

  const sapConfig = query.data?.sapConfig;

  return {
    hideResetPassword: sapConfig?.hideResetPassword === true,
    hideDeleteProfile: sapConfig?.hideDeleteProfile === true,
    defaultJobTitle: sapConfig?.defaultJobTitle ?? undefined,
  };
}

export function useFisSolr(sapIntegrationSettings?: unknown) {
  const fetchedSettings = useSapS4HanaIntegrationSettings();
  const resolvedSettings = sapIntegrationSettings ?? fetchedSettings;
  const hasFisSolr = isFisSolrEnabled(resolvedSettings);
  const { value: fisSolrDisabledByUser, saveValue: setFisSolrDisabledByUser } =
    useUserPreference<boolean>("disableFisSolrSearch", false, {
      skip: !hasFisSolr,
    });
  return {
    sapIntegrationSettings: resolvedSettings,
    hasFisSolr,
    fisSolrDisabledByUser,
    setFisSolrDisabledByUser,
    showFisSolr: hasFisSolr && !fisSolrDisabledByUser,
  };
}

export const isFisSolrEnabled = (settings: unknown): boolean => {
  if (!settings || !isPlainObject(settings)) return false;
  const s = settings as Record<string, unknown>;
  return typeof s.fisSolrUrl === "string" && s.fisSolrUrl.length > 0;
};

export const buildFisSolrUrl = (
  settings: unknown,
  params: {
    itemId: string;
    quoteId: string;
    mode: "ADD" | "REPLACE";
  }
): string | undefined => {
  if (!settings || !isPlainObject(settings)) return undefined;
  const s = settings as Record<string, unknown>;

  const baseUrl = s.fisSolrUrl;
  if (typeof baseUrl !== "string" || baseUrl.length === 0) return undefined;

  try {
    const url = new URL(baseUrl);
    url.searchParams.set("QUOTEID", params.quoteId);
    url.searchParams.set("ITEMID", params.itemId);
    url.searchParams.set("LIMIT", params.mode === "REPLACE" ? "1" : "0");
    url.searchParams.set("MODE", params.mode);
    url.searchParams.set("ENVIRONMENT", DEPLOY_ENV.toUpperCase());
    return url.toString();
  } catch {
    return undefined;
  }
};

export const getDummyMaterialNumbers = (settings: unknown): string[] => {
  if (settings && isPlainObject(settings)) {
    const settingsObj = settings as Record<string, unknown>;
    if (
      "salesQuotationDummyMaterialNumbers" in settingsObj &&
      settingsObj.salesQuotationDummyMaterialNumbers &&
      isArray(settingsObj.salesQuotationDummyMaterialNumbers) &&
      settingsObj.salesQuotationDummyMaterialNumbers.every(
        (item): item is string => typeof item === "string"
      )
    ) {
      return settingsObj.salesQuotationDummyMaterialNumbers;
    }
  }
  return [];
};

export function useItemProductIsConfigurable({
  itemProductArticleNumber,
  product,
  projectId,
  quoteId,
  itemId,
}: {
  itemProductArticleNumber: string | null | undefined;
  product:
    | Pick<PimCustomSupplierProduct, "__typename">
    | (Pick<PimSupplierProduct, "__typename"> & {
        supplierCatalogue: Pick<PimSupplierProduct["supplierCatalogue"], "id">;
      })
    | null
    | undefined;
  projectId: string | null | undefined;
  quoteId: string;
  itemId: string;
}): { isConfigurable: boolean; refetchQueries: string[] } {
  const client = useApolloClient();

  const query = useS4HanaQuoteItemDataHooksQuery({
    client,
    variables: { projectId: projectId!, quoteId, itemId },
    skip: !projectId,
  });

  const quoteSapS4HanaData = query.data?.quoteSapS4HanaData;
  const itemDataQueryData = getDataOrNull(
    query.data?.quoteItemSapS4HanaData
  )?.items?.at(0);

  return {
    isConfigurable: !!(
      quoteSapS4HanaData &&
      itemDataQueryData &&
      itemProductArticleNumber &&
      ((product &&
        product.__typename === "PimSupplierProduct" &&
        quoteSapS4HanaData.configurableProductSupplierCatalogueIds.includes(
          product.supplierCatalogue.id
        )) ||
        !!(
          itemDataQueryData.kmatProductIdLatest &&
          itemDataQueryData.kmatProductIdLatest === itemProductArticleNumber
        ))
    ),
    refetchQueries: [namedOperations.Query.S4HanaQuoteItemDataHooks],
  };
}

export function useQuoteItemIsSyncedWithSapS4Hana({
  projectId,
  quoteId,
  itemId,
}: {
  projectId: string;
  quoteId: string;
  itemId: string;
}) {
  const client = useApolloClient();

  const query = useS4HanaQuoteItemDataHooksQuery({
    client,
    variables: {
      projectId,
      quoteId,
      itemId,
    },
  });

  const sapItemData = getDataOrNull(
    query.data?.quoteItemSapS4HanaData
  )?.items?.at(0);

  return sapItemData ? !!sapItemData.xId : undefined;
}
